import { anyDone, isItemComplete } from "./completion.js";
import { fuzzyMatchAny } from "./fuzzyMatch.js";
import { allTags, hasAnyTag } from "./tags.js";
const matchesAssigneeFilter = (item, filterAssignees) => {
    if (filterAssignees === "all")
        return true;
    const { assignment } = item;
    if (assignment.kind === "everyone" || assignment.kind === "shared")
        return true;
    return assignment.allocations.some((a) => filterAssignees.includes(a.participantId));
};
const sortItems = (items, sort) => {
    if (sort === "alphabetical") {
        return [...items].sort((a, b) => a.name.localeCompare(b.name));
    }
    // "dueDate" only meaningful for todos; for packing items fall through to manual
    // "manual" = preserve fractional index order (caller is expected to pass pre-sorted by order)
    return items;
};
// `field` (issue #188) decides what "complete" means for the status filter and the
// completed-to-bottom sort: a trip list asks whether a row is packed, a template list
// whether it is staged.
export const applyView = (items, cfg, participants, field = "completions") => {
    const complete = (item) => isItemComplete(item, participants, field);
    const partial = (item) => !complete(item) && anyDone(item, field);
    // 1. Filter assignees
    let result = items.filter((item) => matchesAssigneeFilter(item, cfg.filterAssignees));
    // 1b. Filter by the search box (name + description + notes + tags), issues #83/#99.
    if (cfg.query.trim() !== "") {
        result = result.filter((item) => fuzzyMatchAny(cfg.query, item.name, item.description, item.notes, ...allTags(item)));
    }
    // 1c. Filter by tag (issue #99) — OR across the selected tags.
    if (cfg.filterTags.length > 0) {
        result = result.filter((item) => hasAnyTag(item, cfg.filterTags));
    }
    // 2. Filter status
    if (cfg.filterStatus !== "all") {
        const wantComplete = cfg.filterStatus === "complete";
        result = result.filter((item) => complete(item) === wantComplete);
    }
    // 3. Sort
    result = sortItems(result, cfg.sort);
    // 4. completedToBottom — only meaningful when showing all statuses. Three bands:
    // not started, partially done (some but not all required checks), fully done.
    if (cfg.completedToBottom && cfg.filterStatus === "all") {
        const notStarted = result.filter((item) => !partial(item) && !complete(item));
        const partiallyDone = result.filter(partial);
        const done = result.filter(complete);
        result = [...notStarted, ...partiallyDone, ...done];
    }
    return result;
};
// The to-do equivalent (issue #188 — lifted out of `tripDetailStore` so the template
// editor applies the identical view). To-dos carry the same per-participant completion
// as items but their sort also understands due dates, so they don't route through
// `applyView`'s item-shaped filters. Input is expected pre-sorted by fractional index,
// same contract as `applyView`'s "manual".
export const applyTodoView = (todos, cfg, participants) => {
    const done = (t) => isItemComplete(t, participants);
    // 0 = not started, 1 = partially done, 2 = fully done.
    const band = (t) => (done(t) ? 2 : anyDone(t) ? 1 : 0);
    let r = todos;
    // Search box (issue #83) — fuzzy match on name/description/notes, "" = no filter.
    if (cfg.query.trim() !== "") {
        r = r.filter((t) => fuzzyMatchAny(cfg.query, t.name, t.description, t.notes, ...allTags(t)));
    }
    // Tag filter (issue #99) — OR across the selected tags, same rule as applyView.
    if (cfg.filterTags.length > 0)
        r = r.filter((t) => hasAnyTag(t, cfg.filterTags));
    if (cfg.filterStatus !== "all") {
        const wantComplete = cfg.filterStatus === "complete";
        r = r.filter((t) => done(t) === wantComplete);
    }
    if (cfg.sort === "alphabetical")
        r = [...r].sort((a, b) => a.name.localeCompare(b.name));
    else if (cfg.sort === "dueDate")
        r = [...r].sort((a, b) => (a.dueDate ?? "9999-99-99").localeCompare(b.dueDate ?? "9999-99-99"));
    if (cfg.completedToBottom)
        r = [...r].sort((a, b) => band(a) - band(b));
    return r;
};
export const applyNotesView = (notes, cfg) => {
    let r = notes;
    if (cfg.query.trim() !== "")
        r = r.filter((n) => fuzzyMatchAny(cfg.query, n.body, ...allTags(n)));
    if (cfg.filterTags.length > 0)
        r = r.filter((n) => hasAnyTag(n, cfg.filterTags));
    return cfg.sort === "dateAdded" ? [...r].sort((a, b) => b.createdAt - a.createdAt) : r;
};
//# sourceMappingURL=applyView.js.map